import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Award, Heart, Sparkles } from "lucide-react";

// Adama's photo for About page (local file first)
const ADAMA_PHOTO = "/images/about-stylist.jpg";
const IMAGE_VERSION = "20260312";

const values = [
  {
    icon: Sparkles,
    title: "Excellence",
    description: "Every service is executed with precision and attention to detail.",
  },
  {
    icon: Heart,
    title: "Care",
    description: "I treat every client with warmth and respect, creating a welcoming environment.",
  },
  {
    icon: Award,
    title: "Quality",
    description: "I use only premium products and techniques for lasting results.",
  },
];

export default function AboutPage() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="luxury-page-title text-3xl md:text-4xl mb-4">
            Meet the Stylist
          </h1>
          <p className="text-[#666] max-w-lg mx-auto">
            The stylist behind DadaDeLuxe
          </p>
        </div>
      </section>

      {/* Main About Section */}
      <section 
        data-testid="about-section" 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
      >
        <div ref={addRevealRef} className="reveal glass-card p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Image - Full body */}
            <div className="flex justify-center">
              <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-lg bg-[#c5c8ca]">
                <img
                  src={`${ADAMA_PHOTO}?v=${IMAGE_VERSION}`}
                  alt="Adama - DadaDeLuxe Stylist"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 768px) 90vw, 420px"
                />
              </div>
            </div>
            
            {/* Text */}
            <div className="text-center md:text-left">
              <h2 className="script-heading text-2xl md:text-3xl mb-4">
                Hey! I'm Adama
              </h2>
              <div className="space-y-4 text-[#666] leading-relaxed">
                <p>
                  I'm a 21-year-old hairstylist with over five years of experience 
                  specializing in wigs, sew-ins, and quick weaves. I'm based in West 
                  Philadelphia and am the owner of The Luxe Loft, where I provide a 
                  comfortable, elevated space for my clients.
                </p>
                <p>
                  I offer hair-included services using hair from The Luxe Loft, making 
                  it easy to book everything you need in one place. I take pride in 
                  clean work, attention to detail, and creating styles that not only 
                  look good but last.
                </p>
                <p>
                  When you book with me, you're choosing consistency, quality, and a 
                  stylist who truly cares about your overall experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div ref={addRevealRef} className="reveal text-center mb-8">
          <h2 className="script-heading text-2xl">What I Stand For</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {values.map((value, index) => (
            <div
              key={value.title}
              ref={addRevealRef}
              data-testid={`value-card-${index}`}
              className="reveal glass-card p-6 text-center"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 bg-[#F0F0F0] rounded-full flex items-center justify-center mx-auto mb-4">
                <value.icon className="text-[#111]" size={24} />
              </div>
              <h3 className="font-medium text-lg mb-2">{value.title}</h3>
              <p className="text-[#666] text-sm">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h2 className="script-heading text-2xl md:text-3xl mb-4">
            Let's Create Something Beautiful
          </h2>
          <p className="text-[#666] mb-6">
            Ready to experience the DadaDeLuxe difference?
          </p>
          <Link
            to="/book"
            data-testid="about-book-btn"
            className="btn-black inline-block"
          >
            Book Appointment
          </Link>
        </div>
      </section>
    </div>
  );
}
